export type ThinkingLevel = 'off' | 'low' | 'medium' | 'high'

export interface AnthropicThinkingConfig {
  thinking?: { type: 'adaptive' }
  output_config?: { effort: Exclude<ThinkingLevel, 'off'>, display: 'summarized' }
}

export interface OpenAIResponsesReasoningConfig {
  effort: 'low' | 'medium' | 'high'
  summary: 'auto'
}

function supportsAnthropicAdaptiveEffort(model: string): boolean {
  return /claude[-/](?:(?:opus|sonnet)[-/]4[-.][6-9]|(?:opus|sonnet|haiku|fable|mythos)[-/]5(?:[-.]\d+)?)(?:[-./]|$)/i.test(model)
}

export function isAnthropicForcedToolChoiceUnsupportedModel(model: string): boolean {
  return /claude[-/](?:opus[-/]5[-.]5|sonnet[-/]5[-.]5|fable[-.]5\.1|mythos[-.]5\.1)(?:[-./]|$)/i.test(model)
}

export function getAnthropicThinkingConfig(model: string, level: ThinkingLevel): Partial<AnthropicThinkingConfig> {
  if (level === 'off' || !supportsAnthropicAdaptiveEffort(model))
    return {}

  return {
    thinking: { type: 'adaptive' },
    output_config: { effort: level, display: 'summarized' },
  }
}

export function getGeminiThinkingConfig(model: string, level: ThinkingLevel): Record<string, unknown> {
  if (level === 'off')
    return {}

  if (/gemini-2\.5/i.test(model)) {
    const thinkingBudget = {
      low: 1024,
      medium: 8192,
      high: /flash/i.test(model) ? 24576 : 32768,
    } satisfies Record<Exclude<ThinkingLevel, 'off'>, number>

    return {
      thinkingConfig: {
        includeThoughts: true,
        thinkingBudget: thinkingBudget[level],
      },
    }
  }

  if (!/gemini-3(?:\.|-|$)/i.test(model))
    return {}

  const thinkingLevel = {
    low: 'low',
    medium: 'medium',
    high: 'high',
  } satisfies Record<Exclude<ThinkingLevel, 'off'>, 'low' | 'medium' | 'high'>

  return { thinkingConfig: { includeThoughts: true, thinkingLevel: thinkingLevel[level] } }
}

export function getOpenAIResponsesReasoningConfig(level: ThinkingLevel): OpenAIResponsesReasoningConfig | undefined {
  if (level === 'off')
    return undefined

  return {
    effort: level,
    summary: 'auto',
  }
}

export function getOpenAICompatibleReasoningConfig(model: string, level: ThinkingLevel): Record<string, string> {
  if (level === 'off' || !/\bgpt(?:[-./]|$)/i.test(model))
    return {}
  return { reasoning_effort: level }
}
